import React from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { LoadPhase, SkeletonCard } from "../../../components/ui";
import { ROUTES } from "../../../routes/paths";
import MobileChartReadout from "./MobileChartReadout";
import { MONTHS } from "../types/dashboard.types";
import type { MonthlyIncome } from "../types/dashboard.types";
import { BRAND, BRAND_DARK, EMPTY_DISC } from "../chartTheme";

const formatRupees = (value: number) => `₹${value.toLocaleString("en-IN")}`;

/**
 * How the plot is proportioned, and how wide a month's column is.
 *
 * Every month is drawn as a full-height track in the tint that means "a bar
 * goes here", with what it actually took filled up from the bottom. That is
 * what carries the plot: twelve tracks is a shape the eye reads as a year at a
 * glance, where twelve marks of different heights floating on nothing is a
 * scatter of debris — and on a card this size most of those marks sit at the
 * floor, so there was very little to see.
 *
 * `PLOT_HEIGHT` is the height of the thing a finger presses as much as it is
 * the plot's. 40px was a legible shape and a poor target: a track is 10px wide,
 * so the only generous axis a column has is its height, and 56 gets the press
 * area close to the 44px a phone wants without the card growing a band of its
 * own.
 *
 * `BAR_WIDTH` has been wrong in both directions. Left to fill its share of the
 * row a bar comes out about 20px across, which at this height is a fat capsule
 * rather than a bar; cut to 6 it is a hairline with nothing to it. Ten is a
 * bar: solid enough to read as a column, with the rest of the month's share
 * left as air either side so it stays under its own initial — and that air
 * belongs to the press target, which is the whole column rather than the track.
 *
 * `MIN_FILL` is what a month that took almost nothing shows inside its track —
 * enough to be a mark rather than a line, and rounded, so a lean month is still
 * a point in the column rather than a gap in the plot.
 */
const PLOT_HEIGHT = 56;
const BAR_WIDTH = 10;
const MIN_FILL = 6;

/**
 * How big the total is set, which depends on how long it is.
 *
 * 34px is the size the figure is meant to be read at, and it is what all but the
 * largest practice ever sees: at 360px of screen the card leaves about 190px
 * beside the chip in its corner, which carries a total into the crores. Past
 * that the number has nowhere to go — it is one word with no break in it, so it
 * would not wrap, and a rupee figure is the one thing on this screen that must
 * not be shortened or clipped to make it fit. So it steps down instead, which
 * costs a practice in its tenth crore a few points of type and nobody else
 * anything.
 */
const headlineSize = (figure: string) =>
  figure.length > 12
    ? "text-[24px]"
    : figure.length > 10
    ? "text-[28px]"
    : "text-[34px]";

/** The card's own footprint, so the block that stands in for it is the size of
 *  the card that lands in its place — the caption, the figure, the readout, the
 *  plot and the row of initials, plus the padding around them. */
const FOOTPRINT = "h-[214px] rounded-card";

/** The same figure as a floor rather than a ceiling, for the card itself: a
 *  total long enough to wrap — a workspace in its second crore — would be
 *  clipped by a fixed height, and the block above is the size the card is
 *  every other day of its life. */
const FLOOR = "min-h-[214px]";

interface EarningsHeroProps {
  /** Rupees taken in all year. `null` reads as ₹0, for the reason
   *  `EarningsCard` gives: the total of nothing measured is nothing. */
  total: number | null;
  /** A rupee figure per month of the current year, as the endpoint sends it. */
  data: MonthlyIncome[];
  /** Whether this workspace has taken any money yet. */
  empty?: boolean;
  /** Where the dashboard call is. */
  phase?: LoadPhase;
}

/**
 * What the phone's dashboard opens on: the money, and the year it came in over.
 *
 * The one card on the mobile screen that is filled rather than outlined. A
 * phone is read a band at a time, so the screen needs a place for the eye to
 * land first, and on a practice's dashboard that is the takings — everything
 * below it is a count of what produced them. It carries the screen's one hero
 * figure; every other card leads with a stat-tile number, a step down from it.
 *
 * The plot is twelve buttons rather than a Recharts bar chart. What a charting
 * library would bring to a plot this size is the hover layer, and a phone has
 * no pointer to hover with — a tooltip on a touch screen opens under the finger
 * that asked for it. So the months are pressed instead and the answer is written
 * where it can be read: the readout line above the plot, which holds the year's
 * best month until one is asked for by name. That is the whole of what the
 * tooltip was for, in a place a thumb is not covering.
 *
 * The card the desktop shows — `EarningsCard`, with its axis and its per-month
 * tooltip — is still what a pointer gets. Which of the two renders is
 * `DashboardPage`'s decision — see `useIsMobile`.
 *
 * A card with a link in it rather than a card that is a link: the months are
 * pressed now, and a button inside an anchor is neither valid nor pressable. The
 * way to the takings screen is the chip at the top right, which is also where it
 * reads as a heading for the figure under it.
 */
const EarningsHero: React.FC<EarningsHeroProps> = ({
  total,
  data,
  empty = false,
  phase = "ready",
}) => {
  /**
   * Which month is being asked about, as an index into `columns` below, or
   * `null` for the card as it opens.
   *
   * Pressing the month that is already selected clears it, so the readout can be
   * put back to the year's own summary without hunting for somewhere neutral to
   * press — on a card this size there is nowhere neutral.
   */
  const [selected, setSelected] = React.useState<number | null>(null);

  const headline = empty ? 0 : total ?? 0;

  // The same rule `EarningsCard` goes by: no series is a year the backend has
  // not counted, and a row of empty tracks is the shape of the chart that will
  // land here rather than a year that took nothing.
  const showsEmptyYear = empty || data.length === 0;

  // What the tallest bar is measured against. A year whose every month is zero
  // has no scale to speak of, so the guard leaves every track empty rather than
  // dividing by nothing.
  const peak = Math.max(...data.map((month) => month.amount), 0);

  /**
   * The twelve columns the plot is drawn from — the series itself, or a year of
   * flat months standing in for one that has not been counted.
   *
   * One list for both rows below rather than the series for the bars and
   * `MONTHS` for the labels: a backend that sends a short year would otherwise
   * put eight bars over twelve initials, and every bar would be under the wrong
   * month.
   */
  const columns: MonthlyIncome[] = showsEmptyYear
    ? MONTHS.map((month) => ({ month, amount: 0, status: "future" as const }))
    : data;

  /**
   * How much of a month's track is filled.
   *
   * Zero for a month that has not happened, which leaves its track empty — and
   * that is the whole of what says so, since the track is already the shape of
   * the bar to come. No recessive grey mark inside it: a second mark meaning
   * "nothing" inside a shape that already means "nothing" is two ways of saying
   * it, and on a 10px column they would be hard to tell apart anyway.
   */
  const fillHeight = (month: MonthlyIncome) =>
    peak <= 0 || month.status === "future"
      ? 0
      : Math.max(MIN_FILL, Math.round((month.amount / peak) * PLOT_HEIGHT));

  /**
   * The year's best month, which is what the readout says while nothing is
   * selected.
   *
   * A measured fact about the series rather than a hint about how to use the
   * card — "tap a bar" is a line the reader needs once and then reads for the
   * life of the account. It is also the one direct label the plot carries:
   * twelve figures over twelve 10px columns is the scatter of numbers that makes
   * a chart unreadable, and the extreme is the one worth naming.
   *
   * `null` for a year with nothing in it, where there is no best month to have.
   */
  const best = React.useMemo(() => {
    if (showsEmptyYear || peak <= 0) return null;
    return columns.reduce((leader, column) =>
      column.amount > leader.amount ? column : leader
    );
  }, [columns, peak, showsEmptyYear]);

  /**
   * What the filled part of a track is drawn in — the same rule `EarningsCard`
   * gives its Cells, less the one case the track has taken over: a month gone is
   * the brand, and the month in hand is outlined rather than filled, because its
   * figure is still being counted.
   *
   * The month being asked about takes the darker of the two brand steps. Nothing
   * is being told apart by it — which month is selected is said in words on the
   * readout line and again by the inked initial under the column — so it is a
   * step of one hue doing the work of emphasis rather than a second category.
   *
   * Styles rather than classes, and straight from `chartTheme`, which is how
   * `EmptyMonthAxis` draws its own marks too. A background class built at render
   * time out of a hex is one Tailwind never sees in the source and so never
   * generates — the bar would come out with no fill at all.
   */
  const fillOf = (
    column: MonthlyIncome,
    isSelected: boolean
  ): React.CSSProperties => {
    if (isSelected) return { backgroundColor: BRAND_DARK };
    return column.status === "current"
      ? {
          backgroundColor: "#FFFFFF",
          // An inset ring, since a border on something this narrow would be laid
          // inside the bar's own width and close it up.
          boxShadow: `inset 0 0 0 1.5px ${BRAND_DARK}`,
        }
      : { backgroundColor: BRAND };
  };

  /** What a month reads as to a screen reader, and what the readout writes out
   *  once it is pressed. A month that has not happened has no figure to give —
   *  "₹0" of next March would be a measurement rather than a blank. */
  const monthAmount = (column: MonthlyIncome) =>
    column.status === "future"
      ? "No earnings yet"
      : formatRupees(column.amount);

  if (phase !== "ready") {
    return <SkeletonCard phase={phase} className={FOOTPRINT} />;
  }

  const shown = selected === null ? null : columns[selected] ?? null;
  const figure = formatRupees(headline);

  return (
    <section
      // A wash of the brand rather than the brand itself: the card is the one
      // the eye is meant to land on, and on a phone it is landed on several
      // times a day — a filled teal panel is a thing to get past by the third
      // visit. The tint lifts it off the page without shouting, and the figure
      // over it stays the app's own ink rather than white on a colour.
      //
      // `brand-soft` into white so the fill has some depth to it; the border is
      // a step further into the brand than the fill, which is what keeps the
      // card's edge from disappearing into the page behind it. `rounded-card`
      // is the corner every card on this screen wears, taken without the lift
      // that comes with `card-surface` — the fill is doing that job here. See
      // `MobileDashboard`.
      className={`rounded-card border border-[#C2EFF3] bg-gradient-to-br from-brand-soft to-white p-5 ${FLOOR}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[13px] text-ink-muted">
            {/* The caption labels the figure, not the plot — the same split
                `EarningsCard` makes, so a total with nothing to chart is still
                named as a total. */}
            {showsEmptyYear && headline === 0
              ? "No earnings yet"
              : "Total earnings"}
          </p>
          {/* The screen's one hero figure. Proportional digits rather than
              tabular: nothing lines up under it, and at this size tabular
              figures leave a number like ₹121 looking gappy. */}
          <p
            className={`mt-1 font-semibold leading-none tracking-tight text-ink ${headlineSize(
              figure
            )}`}
          >
            {figure}
          </p>
        </div>

        {/* The way to the takings screen, which this card is the summary of. A
            link rather than a pressable card, so the status bar says where it
            leads and the months below it stay pressable. */}
        <Link
          to={ROUTES.revenue}
          className="flex-shrink-0 inline-flex items-center gap-1 rounded-full bg-white px-3 py-1.5 text-[11px] font-medium text-brand-dark shadow-[0_1px_2px_rgba(30,31,36,0.06)] transition-opacity active:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
        >
          This year
          <ChevronRight aria-hidden className="w-3.5 h-3.5" />
        </Link>
      </div>

      {/* What was just pressed, or the year's best month until something is —
          see `MobileChartReadout`, which both of the phone's charts read out
          through so the same gesture answers the same way on either. */}
      <div className="mt-3">
        <MobileChartReadout
          label={shown?.month}
          value={shown ? monthAmount(shown) : null}
          swatch={BRAND_DARK}
          fallback={
            best && (
              <>
                <span className="text-ink-subtle">Best month</span>
                <span className="font-medium text-ink-muted">{best.month}</span>
              </>
            )
          }
        />
      </div>

      {/* The year, and only as much of one as a phone can carry: a column per
          month, a press per column, and an initial under it.

          The fills are the brand teal, which sits under 3:1 against a surface
          this light. That is the condition `chartTheme` names: it may be used
          where the chart carries visible labels of its own, and this one has the
          initials below it, the readout above it and the total over that. */}
      <div role="group" aria-label="Earnings by month" className="mt-2.5">
        {/* A column per month, each an equal share of the row and each the whole
            press target, with the track centred in it rather than filling it —
            see `BAR_WIDTH`. No gap between the columns: the air either side of a
            track is what the column already leaves, it is the half of the target
            a thumb actually lands on, and a gap on top of it would push the
            twelve of them out of line with the initials below. */}
        <div className="flex items-end" style={{ height: PLOT_HEIGHT }}>
          {columns.map((column, index) => {
            const isSelected = index === selected;
            const filled = fillHeight(column);

            return (
              <button
                key={`${column.month}-${index}`}
                type="button"
                // The month and its figure, so the plot reads as a list of
                // twelve facts rather than as twelve unlabelled heights — which
                // is what the desktop card's tooltip says, and the reason this
                // row is no longer hidden from screen readers.
                aria-label={`${column.month}: ${monthAmount(column)}`}
                aria-pressed={isSelected}
                onClick={() => setSelected(isSelected ? null : index)}
                className="flex-1 flex items-end justify-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
                style={{ height: PLOT_HEIGHT }}
              >
                {/* The track: the month's whole column, in the tint `chartTheme`
                    keeps for a shape a chart goes in rather than for a chart.
                    `overflow-hidden` is what holds the fill's own rounding
                    inside the track's at the top. */}
                <span
                  className="relative overflow-hidden rounded-full"
                  style={{
                    width: BAR_WIDTH,
                    height: PLOT_HEIGHT,
                    backgroundColor: EMPTY_DISC,
                  }}
                >
                  {filled > 0 && (
                    <span
                      className="absolute inset-x-0 bottom-0 rounded-full"
                      style={{ height: filled, ...fillOf(column, isSelected) }}
                    />
                  )}
                </span>
              </button>
            );
          })}
        </div>
        {/* The axis. Hidden from screen readers: the buttons above carry their
            own month names, and a second row of twelve initials would be the
            axis read out twice. The month being asked about is inked and
            weighted, which ties the readout to the column it came from without a
            mark of its own. */}
        <div aria-hidden className="mt-2 flex">
          {columns.map((column, index) => (
            <span
              key={`${column.month}-${index}`}
              className={`flex-1 text-center text-[10px] leading-none ${
                index === selected
                  ? "font-semibold text-brand-dark"
                  : "font-medium text-ink-subtle"
              }`}
            >
              {column.month.charAt(0)}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default EarningsHero;
